import React, { useState } from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { PyrenzBlueButton } from '@client/theme'
import {
  ModelSelector,
  LoraSelector,
  SamplerSelector,
  HordeSettings,
  AspectSelector,
  GenerationParams,
  ImageDisplay,
  useImageGen,
  FaceFixer,
  Textarea,
  StylePresetSelector,
  ImageProcessingSelector,
  Sidebar,
  MobileNav
} from '@components'
import { LoraType } from '@shared-types'

export function ImageGen() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const {
    models,
    selectedModel,
    setSelectedModel,
    loras,
    selectedLoras,
    setSelectedLoras,
    selectedSampler,
    setSelectedSampler,
    images,
    isLoading,
    status,
    autoDowngrade,
    allowSlowWorkers,
    allowNSFW,
    promptFilter,
    trustedWorkers,
    karras,
    tiling,
    hiresFix,
    stripBackground,
    transparentBackground,
    faceFixerMethod,
    faceFixerStrength,
    width,
    height,
    handleHordeSettingChange,
    steps,
    cfgScale,
    clipSkip,
    handleParamChange,
    setWidth,
    setHeight,
    setFaceFixerMethod,
    setFaceFixerStrength,
    applyPreset,
    selectLora,
    handleSubmit,
  } = useImageGen()

  const [promptInput, setPromptInput] = useState('')
  const [negativeInput, setNegativeInput] = useState('')
  const [processingMethod, setProcessingMethod] = useState<'img2img' | 'inpainting' | 'outpainting' | 'remix'>('img2img')
  const [sourceImages, setSourceImages] = useState<string[]>([])
  const [showLoginModal, setShowLoginModal] = useState(false)

  const handleGenerate = () => {
    handleSubmit(promptInput, negativeInput, processingMethod, sourceImages)
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {!isMobile && <Sidebar />}

      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 2, md: 4 },
          py: 4,
        }}
      >
        <Typography variant="h5" mb={2} textAlign="center">
          Image Generator (Free)
        </Typography>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            overflowY: 'auto',
            pr: 1,
          }}
        >
          <Textarea
            value={promptInput}
            onChange={(e) => setPromptInput(e.target.value)}
            label="Prompt"
            placeholder="What do you want?"
            showTokenizer
          />

          <Textarea
            value={negativeInput}
            onChange={(e) => setNegativeInput(e.target.value)}
            label="Negative Prompt"
            placeholder="What should be avoided?"
            showTokenizer
          />

          <StylePresetSelector onSelect={(key, preset) => applyPreset(preset)} />

          <ModelSelector models={models} selectedModel={selectedModel} onChange={setSelectedModel} />

          <LoraSelector
            loras={loras as LoraType[]}
            selectedLoras={selectedLoras as LoraType[]}
            onChange={(newLoras) => {
              setSelectedLoras(newLoras as any)
              const loraWithPrompt = newLoras.find((l) => (l as any).preset_prompt)
              if (loraWithPrompt) {
                selectLora({
                  id: String(loraWithPrompt.id),
                  preset_prompt: (loraWithPrompt as any).preset_prompt,
                })
              }
            }}
            maxSelect={5}
          />

          <SamplerSelector selectedSampler={selectedSampler} onChange={setSelectedSampler} />

          <HordeSettings
            autoDowngrade={autoDowngrade}
            allowSlowWorkers={allowSlowWorkers}
            allowNSFW={allowNSFW}
            promptFilter={promptFilter}
            trustedWorkers={trustedWorkers}
            karras={karras}
            tiling={tiling}
            hiresFix={hiresFix}
            stripBackground={stripBackground}
            transparentBackground={transparentBackground}
            onChange={handleHordeSettingChange}
          />

          <GenerationParams steps={steps} cfgScale={cfgScale} clipSkip={clipSkip} onChange={handleParamChange} />
          <AspectSelector width={width} height={height} onChange={(w, h) => { setWidth(w); setHeight(h) }} />
          <FaceFixer method={faceFixerMethod} strength={faceFixerStrength} setMethod={setFaceFixerMethod} setStrength={setFaceFixerStrength} />

          <ImageProcessingSelector
            onChange={(method, images) => {
              setProcessingMethod(method)
              setSourceImages(images)
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <PyrenzBlueButton
            variant="contained"
            onClick={handleGenerate}
            sx={{ minWidth: 200, py: 1.5 }}
          >
            Generate
          </PyrenzBlueButton>
        </Box>

        <Box sx={{ mt: 4 }}>
          <ImageDisplay images={images} isLoading={isLoading} status={status} onReset={() => setSourceImages([])} />
        </Box>
      </Box>

      {isMobile && <MobileNav setShowLoginModal={setShowLoginModal} />}
    </Box>
  )
}
